/**
 * Registro das falhas que chegam ao RootErrorBoundary.
 *
 * A tela de erro é bonita de propósito — o usuário não deve ver stack trace
 * nenhum. O custo disso é que, sem gravar em algum lugar, uma falha em
 * produção só existe no console do navegador de quem a viu. Este módulo é o
 * outro lado: a tela fica limpa, e o erro chega até você.
 *
 * Três cuidados, em ordem de importância:
 *
 * 1. O import do Firebase é dinâmico. Este arquivo é alcançado a partir do
 *    `main.tsx`, e um import estático colocaria o `firebase-vendor` como
 *    dependência do chunk de entrada — o Firestore passaria a ser baixado no
 *    primeiro paint da landing só por causa de um caminho de erro que quase
 *    nunca roda.
 * 2. Nada aqui pode estourar. Se a gravação falhar (offline, regra, cota), a
 *    tela de erro não pode virar uma tela em branco por cima do erro original.
 * 3. Uma gravação por código por sessão. Um erro de render em loop, ou o
 *    StrictMode montando duas vezes, escreveria o mesmo documento repetidas
 *    vezes.
 */

const CHAVE_SESSAO = "ecclesiato:errosRegistrados";

export type ErroRegistravel = {
  codigo: string;
  mensagem: string;
  stack?: string;
  status?: number | null;
};

/** Corta o texto no limite que a regra do Firestore aceita, com reticência
 *  para deixar claro que houve corte. */
function limitar(texto: string | undefined, max: number) {
  if (!texto) return null;
  return texto.length > max ? `${texto.slice(0, max - 1)}…` : texto;
}

function jaRegistrado(codigo: string) {
  try {
    const bruto = sessionStorage.getItem(CHAVE_SESSAO);
    const codigos: unknown = bruto ? JSON.parse(bruto) : [];
    if (!Array.isArray(codigos)) return false;
    if (codigos.includes(codigo)) return true;
    // Guarda no máximo 20 códigos: é sessão, não histórico.
    sessionStorage.setItem(
      CHAVE_SESSAO,
      JSON.stringify([...codigos.slice(-19), codigo]),
    );
    return false;
  } catch {
    // sessionStorage bloqueado (modo privado, cookies negados): grava sem
    // deduplicar, que é melhor do que não gravar.
    return false;
  }
}

export async function registrarErro(erro: ErroRegistravel) {
  try {
    if (jaRegistrado(erro.codigo)) return;

    const payload = {
      codigo: erro.codigo,
      mensagem: limitar(erro.mensagem, 500) ?? "(sem mensagem)",
      stack: limitar(erro.stack, 4000),
      status: typeof erro.status === "number" ? erro.status : null,
      url: limitar(window.location.href, 500),
      rota: limitar(window.location.pathname, 200),
      userAgent: limitar(navigator.userAgent, 300),
      // Melhor esforço: o boundary vive fora do AppProviders, e a sessão do
      // Firebase pode nem ter sido restaurada quando o erro aconteceu.
      uid: null as string | null,
      churchId: limitar(
        localStorage.getItem("lastSelectedChurchId") ?? undefined,
        128,
      ),
    };

    if (import.meta.env.DEV) {
      // Em desenvolvimento não polui a coleção — mostra o que seria enviado.
      console.warn("[error-log] seria gravado em errorLogs:", payload);
      return;
    }

    const [{ db, auth }, { addDoc, collection, serverTimestamp }] =
      await Promise.all([import("./firebase"), import("firebase/firestore")]);

    await addDoc(collection(db, "errorLogs"), {
      ...payload,
      uid: auth.currentUser?.uid ?? null,
      createdAt: serverTimestamp(),
    });
  } catch {
    // Silêncio de propósito: ver a tela de erro é mais importante do que
    // registrar o erro.
  }
}
